import { currentSeason } from './rotationScore.js'
import { localDayISO } from './localDay.js'
import { complimentCountForLog } from './complimentEvents.js'

// Local calendar day — see src/lib/localDay.js for why this must never be UTC.
export function todayISO(date = new Date()) {
  return localDayISO(date)
}

export function displayDate(date = new Date()) {
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

// Pre-log prompt shown as a small label above "Help me pick" and the search box.
// Founder ruling 2026-09-30: before a scent is chosen, Home must not claim
// "What I'm wearing right now" — nothing is worn yet. The label is the same all
// day so it is predictable; §3.4 time-of-day awareness survives as a softer
// helper line later in the day instead of a morning nudge at 9pm.
export function sotdPromptCopy(date = new Date()) {
  const hour = date.getHours()
  const label = 'Choose your scent of the day'
  if (hour < 17) return { label, helper: '' }
  if (hour < 21) return { label, helper: 'No pressure — log it whenever you land on one' }
  return { label, helper: 'Didn’t log today? That’s fine — log it now or skip to tomorrow' }
}

export function arrayValue(value) {
  if (Array.isArray(value)) return value.filter(Boolean)
  if (typeof value === 'string') {
    return value.split(',').map(v => v.trim()).filter(Boolean)
  }
  return []
}

export function concentrationLabel(fragrance) {
  return fragrance?.concentration || fragrance?.concentrations?.[0] || ''
}

function isActiveCandidate(fragrance) {
  return !fragrance?.given_away && !fragrance?.finished && !fragrance?.finished_at
}

export function noteChips(fragrance, limit = 4) {
  return [
    ...arrayValue(fragrance?.accord_tags),
    ...arrayValue(fragrance?.top_notes),
    ...arrayValue(fragrance?.mid_notes),
    ...arrayValue(fragrance?.base_notes),
  ].filter((value, index, arr) => arr.findIndex(v => v.toLowerCase() === value.toLowerCase()) === index).slice(0, limit)
}

export function logDate(log) {
  return log?.worn_on || log?.wear_date || ''
}

export function isTodayLog(log) {
  return logDate(log) === todayISO()
}

export function findFragranceForLog(log, fragrances) {
  if (!log) return null
  return fragrances.find(f => f.id === log.fragrance_id || (f.sizes || []).some(size => size.id === log.fragrance_id))
    || fragrances.find(f =>
      String(f.name || '').toLowerCase() === String(log.fragrance_name || '').toLowerCase()
      && String(f.house || '').toLowerCase() === String(log.house || '').toLowerCase()
    )
    || null
}

export function statsForFragrance(fragrance, logs) {
  const ids = new Set([fragrance?.id, ...(fragrance?.sizes || []).map(size => size.id)].filter(Boolean))
  const name = String(fragrance?.name || '').toLowerCase()
  const house = String(fragrance?.house || '').toLowerCase()
  const matches = logs.filter(log =>
    ids.has(log.fragrance_id)
    || (String(log.fragrance_name || '').toLowerCase() === name && String(log.house || '').toLowerCase() === house)
  )
  const compliments = matches.reduce((sum, log) => sum + complimentCountForLog(log), 0)
  const sprays = matches.reduce((sum, log) => sum + Number(log.sprays || 0), 0)
  const ratings = matches
    .map(log => Number(log.rating ?? log.user_rating ?? log.wear_rating ?? 0))
    .filter(Boolean)
  return {
    wears: matches.length,
    compliments,
    sprays,
    avgRating: ratings.length ? ratings.reduce((sum, value) => sum + value, 0) / ratings.length : null,
    logs: matches,
  }
}

export function cardSummary(fragrance, stats) {
  const accords = noteChips(fragrance, 2)
  const accordText = accords.length ? accords.join(' and ') : fragrance?.scent_family || 'your collection data'
  if (stats?.wears > 0) {
    const complimentText = stats.compliments > 0
      ? `${stats.compliments} compliment${stats.compliments === 1 ? '' : 's'} logged`
      : 'no compliments logged yet'
    return `${accordText} profile with ${stats.wears} wear${stats.wears === 1 ? '' : 's'} and ${complimentText}.`
  }
  return `${accordText} profile. Log wears to turn this card into your own performance read.`
}

// Wear Model v2 labels — one 'sotd' per day, other labels unlimited.
export const WEAR_LABELS = [
  { value: 'sotd', label: 'SOTD' },
  { value: 'gym', label: 'Gym' },
  { value: 'work', label: 'Work' },
  { value: 'evening', label: 'Evening' },
  { value: 'night', label: 'Night' },
  { value: 'event', label: 'Event' },
  { value: 'other', label: 'Other' },
]

export function wearLabelText(log) {
  const value = log?.wear_label || 'sotd'
  return WEAR_LABELS.find(item => item.value === value)?.label || value
}

export function appliedAtLabel(log) {
  const firstApplication = log?.applications?.find(app => !app.is_reapplication)
    || log?.applications?.[0]
  const value = firstApplication?.applied_at || log?.created_at
  if (!value) return 'today'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'today'
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

export function seasonalPicks(fragrances, limit = 3) {
  const season = currentSeason()
  return fragrances
    .filter(isActiveCandidate)
    .filter(f => arrayValue(f.seasons).map(s => s.toLowerCase()).includes(season.toLowerCase()))
    .slice(0, limit)
}

export function eveningRecommendation(context, wornFragrance, fragrances, logs) {
  const fallback = wornFragrance || fragrances[0]
  const candidates = fragrances.filter(f => isActiveCandidate(f) && f.id !== wornFragrance?.id)
  const byOccasion = term => candidates.find(f =>
    arrayValue(f.occasions).some(o => o.toLowerCase().includes(term))
  )
  const byAccord = terms => candidates.find(f => {
    const notes = noteChips(f, 12).join(' ').toLowerCase()
    return terms.some(term => notes.includes(term))
  })
  const picks = {
    gym: byOccasion('gym') || byAccord(['fresh', 'citrus', 'aquatic']) || candidates[0] || fallback,
    dinner: byOccasion('evening') || byAccord(['amber', 'leather', 'woody']) || candidates[0] || fallback,
    date: byOccasion('date') || byAccord(['vanilla', 'sweet', 'amber']) || candidates[0] || fallback,
    chill: byAccord(['vanilla', 'tonka', 'musk']) || candidates[0] || fallback,
  }
  const pick = picks[context] || picks.dinner
  const stats = statsForFragrance(pick, logs)
  const compliments = stats.compliments > 0
    ? `It has ${stats.compliments} compliment${stats.compliments === 1 ? '' : 's'} in your log.`
    : 'No compliment signal yet; this is based on notes and occasion fit.'
  const whyByContext = {
    gym: 'Keep projection close and clean after sweat.',
    dinner: 'Move into a warmer, dressier register for the table.',
    date: 'Stay warmer and softer while the first scent fades.',
    chill: 'Let the day settle into something comfortable and low-pressure.',
  }
  return {
    fragrance: pick,
    action: pick?.id === wornFragrance?.id ? 'Re-apply' : 'Bridge',
    why: `${whyByContext[context] || whyByContext.dinner} ${compliments}`,
  }
}
